import React from 'react';
import { CalendarCheck, FileCheck2, Hourglass, Clock, Compass, Check, Loader2 } from 'lucide-react';
import { TestType } from '@/models/SharedTypes';
import type { ExamScheduleEntry, ExamStatus } from '@/models/UserTypes';
import { profileService } from '@/services/ProfileService';
import { logger } from '@/utils/logger';
import { LCSW_CLINICAL_HOURS_GOAL, LCSW_SUPERVISION_HOURS_GOAL } from '@/utils/examSchedule';

/**
 * The smart scheduler — the grown-up replacement for the native `<input type=date>`.
 *
 * It is STATUS-FIRST (Pamela's insight: an ASWB date is gated by state-board
 * approval, so most candidates have no date yet). It asks "where are you with
 * your {track} exam?" and captures the right thing per branch: a date, a target
 * window, or accrued hours — so every candidate, at every career stage, leaves
 * with a schedule instead of bouncing off a date wall.
 *
 * Reused by both onboarding (controlled: persist=false, reports via onChange)
 * and the dashboard modal (persist=true: writes via ProfileService itself).
 */

const STATUS_OPTIONS: {
  status: ExamStatus;
  label: string;
  hint: string;
  Icon: React.ComponentType<{ className?: string }>;
}[] = [
  { status: 'scheduled', label: 'I have a date', hint: 'Seat booked (ATT in hand)', Icon: CalendarCheck },
  { status: 'approved', label: 'Approved, booking soon', hint: 'Board approved, no seat yet', Icon: FileCheck2 },
  { status: 'waiting', label: 'Waiting on board approval', hint: 'Application / transcripts in', Icon: Hourglass },
  { status: 'accruing', label: 'Still accruing my hours', hint: 'Mid clinical + supervision hours', Icon: Clock },
  { status: 'exploring', label: 'Just exploring', hint: "Haven't applied yet", Icon: Compass },
];

const trackLabel = (track: TestType): string => {
  switch (track) {
    case TestType.LMSW: return 'LMSW';
    case TestType.LBSW: return 'LBSW';
    default: return 'LCSW';
  }
};

/** Next 12 months as selectable target windows ('YYYY-MM' → 'Month YYYY'). */
function buildWindowOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
}

const todayISO = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export interface ExamSchedulerProps {
  /** The track being scheduled. */
  track: TestType;
  /** Hydrate from an existing entry (dashboard editing an existing schedule). */
  initialEntry?: ExamScheduleEntry | null;
  /** Preselect a status (the /try → onboarding handoff passes the lead's answer). */
  initialStatus?: ExamStatus | null;
  /**
   * When true (default) the scheduler persists via ProfileService and shows a
   * Save button. When false it is a controlled capture that only reports the
   * working entry through onChange (onboarding owns the write at Finish).
   */
  persist?: boolean;
  /** Fired after a successful persist (persist=true). */
  onSaved?: (entry: ExamScheduleEntry | null) => void;
  /** Fired on every working-entry change (so a parent can hold/submit it). */
  onChange?: (entry: ExamScheduleEntry | null) => void;
}

export const ExamScheduler: React.FC<ExamSchedulerProps> = ({
  track,
  initialEntry,
  initialStatus,
  persist = true,
  onSaved,
  onChange,
}) => {
  const windowOptions = React.useMemo(buildWindowOptions, []);

  const [status, setStatus] = React.useState<ExamStatus | null>(
    initialEntry?.status ?? initialStatus ?? null
  );
  const [date, setDate] = React.useState<string>(initialEntry?.date ?? '');
  const [targetWindow, setTargetWindow] = React.useState<string>(initialEntry?.targetWindow ?? '');
  const [clinical, setClinical] = React.useState<string>(
    initialEntry?.hours ? String(initialEntry.hours.clinical) : ''
  );
  const [supervision, setSupervision] = React.useState<string>(
    initialEntry?.hours ? String(initialEntry.hours.supervision) : ''
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const buildEntry = React.useCallback((): ExamScheduleEntry | null => {
    if (!status) return null;
    const entry: ExamScheduleEntry = { status, updatedAt: new Date().toISOString() };
    if (status === 'scheduled' && date) entry.date = date;
    if ((status === 'approved' || status === 'waiting') && targetWindow) entry.targetWindow = targetWindow;
    if (status === 'accruing') {
      entry.hours = { clinical: Number(clinical) || 0, supervision: Number(supervision) || 0 };
    }
    return entry;
  }, [status, date, targetWindow, clinical, supervision]);

  // Report the working entry upward whenever it changes (controlled mode). We
  // intentionally re-emit only on the actual entry inputs, not on onChange's
  // identity (which a parent may recreate each render).
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  React.useEffect(() => {
    onChangeRef.current?.(buildEntry());
  }, [status, date, targetWindow, clinical, supervision, buildEntry]);

  // Switching status clears the stale branch fields so they can't leak into the
  // saved entry (e.g. a scheduled date surviving a switch to "accruing").
  const chooseStatus = (next: ExamStatus) => {
    setStatus(next);
    setDate('');
    setTargetWindow('');
    setClinical('');
    setSupervision('');
    setError(null);
  };

  const isComplete = ((): boolean => {
    switch (status) {
      case 'scheduled': return !!date;
      case 'approved':
      case 'waiting': return !!targetWindow;
      case 'accruing': return true; // 0 hrs is a valid starting point
      case 'exploring': return true;
      default: return false;
    }
  })();

  const handleSave = async () => {
    const entry = buildEntry();
    if (!entry || !isComplete) return;
    setIsSaving(true);
    setError(null);
    try {
      await profileService.setExamScheduleForTestType(track, entry);
      onSaved?.(entry);
    } catch (err) {
      logger.error('ExamScheduler', 'Failed to save exam schedule', { err });
      setError('Could not save your schedule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="font-medium text-neutral-900">
          Where are you with your {trackLabel(track)} exam?
        </p>
        <p className="text-sm text-neutral-500">
          Your date depends on state-board approval. Pick whatever's true today.
        </p>
      </div>

      {/* Status choices — the first, always-answerable question. */}
      <div className="grid gap-2.5">
        {STATUS_OPTIONS.map(({ status: s, label, hint, Icon }) => {
          const selected = status === s;
          return (
            <button
              key={s}
              type="button"
              data-testid={`exam-status-${s}`}
              aria-pressed={selected}
              onClick={() => chooseStatus(s)}
              className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                selected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 bg-neutral-100 hover:border-neutral-300'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  selected ? 'bg-primary-500 text-white' : 'bg-neutral-200 text-neutral-600'
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${selected ? 'text-primary-700' : 'text-neutral-900'}`}>
                    {label}
                  </span>
                  {selected && (
                    <span className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                  )}
                </div>
                <span className="text-sm text-neutral-500">{hint}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Per-branch capture. */}
      {status === 'scheduled' && (
        <div className="space-y-2 pt-1">
          <label className="block text-sm font-medium text-neutral-700">Your exam date</label>
          <input
            type="date"
            value={date}
            min={todayISO()}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
          />
        </div>
      )}

      {(status === 'approved' || status === 'waiting') && (
        <div className="space-y-2 pt-1">
          <label className="block text-sm font-medium text-neutral-700">
            {status === 'approved' ? 'When do you plan to test?' : 'When are you aiming for?'}
          </label>
          <select
            data-testid="exam-window-select"
            value={targetWindow}
            onChange={(e) => setTargetWindow(e.target.value)}
            className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
          >
            <option value="">Pick a target month…</option>
            {windowOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {status === 'approved' && (
            <p className="text-xs text-neutral-500">
              You're approved. The next step is to book your seat with ASWB.
            </p>
          )}
        </div>
      )}

      {status === 'accruing' && (
        <div className="space-y-3 pt-1">
          <p className="text-sm text-neutral-600">
            LCSW eligibility is {LCSW_CLINICAL_HOURS_GOAL.toLocaleString()} clinical +{' '}
            {LCSW_SUPERVISION_HOURS_GOAL} supervision hours. Track your progress:
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-700">Clinical hours</label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  max={LCSW_CLINICAL_HOURS_GOAL}
                  data-testid="exam-hours-clinical"
                  value={clinical}
                  onChange={(e) => setClinical(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
              </div>
              <span className="text-xs text-neutral-400">of {LCSW_CLINICAL_HOURS_GOAL.toLocaleString()}</span>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-700">Supervision hours</label>
              <input
                type="number"
                min={0}
                max={LCSW_SUPERVISION_HOURS_GOAL}
                data-testid="exam-hours-supervision"
                value={supervision}
                onChange={(e) => setSupervision(e.target.value)}
                placeholder="0"
                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              />
              <span className="text-xs text-neutral-400">of {LCSW_SUPERVISION_HOURS_GOAL}</span>
            </div>
          </div>
        </div>
      )}

      {status === 'exploring' && (
        <div className="p-3.5 rounded-xl bg-primary-50 border border-primary-100 text-sm text-primary-700">
          No pressure. We'll build your base with light daily practice and help you
          find your date when you're ready.
        </div>
      )}

      {error && <p className="text-sm text-error">{error}</p>}

      {/* Save is only shown when the scheduler owns persistence (dashboard). In
          controlled mode (onboarding) the parent's own submit is the save. */}
      {persist && status && (
        <button
          type="button"
          onClick={handleSave}
          disabled={!isComplete || isSaving}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 shadow-ledge-brand transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save schedule
        </button>
      )}
    </div>
  );
};

export default ExamScheduler;
